/**
 * DEBUG-ONLY pipeline dump for diagnosing weird redlines.
 *
 * Unlike the structural, text-free `HybridApplyTrace`, this logs the ACTUAL intermediate strings and
 * op values (clause text included) so we can see *why* the diff produced a given redline — e.g. a
 * `delete "Closing (Closing"` next to an `insert "Closing(Closing"`. It routes through `debugLog`,
 * which records ONLY when the operator has turned Debug logging on (see RemoteDebugLog: `pushEntry`
 * returns early unless enabled). So it is inert by default and in production; it emits content only
 * during an explicit debugging session the operator initiated.
 */

import { debugLog } from '../../utils/RemoteDebugLog';
import { Op, RevisionContext } from './types';

/** accepted state = visible baseline with existing deletion spans removed (insertions kept). */
function computeAcceptedBaseline(baselineVisible: string, revisionContext: RevisionContext): string {
  const dels = revisionContext.revisions
    .filter((r) => r.kind === 'deletion')
    .sort((a, b) => a.start - b.start);
  let accepted = '';
  let cursor = 0;
  for (const d of dels) {
    if (d.start < cursor) continue;
    accepted += baselineVisible.slice(cursor, d.start);
    cursor = d.end;
  }
  accepted += baselineVisible.slice(cursor);
  return accepted;
}

/**
 * Dump the full hybrid pipeline for one paragraph. `label` distinguishes 'preview' vs 'apply'.
 * No-op unless Debug logging is enabled.
 */
export function debugHybridPipeline(
  label: string,
  baselineVisible: string,
  cleanTarget: string,
  revisionContext: RevisionContext,
  targetVisible: string,
  ops: Op[],
): void {
  debugLog(`[HybridDebug] ${label}: strings`, {
    baselineVisibleLen: baselineVisible.length,
    baselineVisible,
    cleanTargetLen: cleanTarget.length,
    cleanTarget,
    // The accepted state the AI target SHOULD be an edit of; compare against cleanTarget to see drift.
    acceptedBaseline: computeAcceptedBaseline(baselineVisible, revisionContext),
    // The reconstructed visible target (keep+insert) the ops produce.
    targetVisibleLen: targetVisible.length,
    targetVisible,
  });

  debugLog(`[HybridDebug] ${label}: existing revisions`, {
    count: revisionContext.revisions.length,
    revisions: revisionContext.revisions.map((r) => ({
      kind: r.kind,
      side: r.side ?? null,
      start: r.start,
      end: r.end,
      text: baselineVisible.slice(r.start, r.end),
    })),
  });

  debugLog(`[HybridDebug] ${label}: ops`, {
    count: ops.length,
    // Each op verbatim — this is where "Closing(Closing" vs "Closing (Closing" becomes visible.
    ops: ops.map((o) => ({ kind: o.kind, value: o.value })),
  });
}
